import React, { useState } from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardMedia,
  Alert,
} from '@mui/material';
import { styled } from '@mui/material/styles';

// Стилизованная карточка для предпросмотра изображения
const StyledImagePreview = styled(Card)(({ theme }) => ({
  maxWidth: '100%',
  margin: theme.spacing(2, 0),
  border: `1px solid ${theme.palette.divider}`,
}));

const ImageUrlPicker = ({ open, onClose, onImageSelect }) => {
  const [imageUrl, setImageUrl] = useState('');
  const [altText, setAltText] = useState('');
  const [alignment, setAlignment] = useState('center');
  const [size, setSize] = useState('medium');
  const [caption, setCaption] = useState('');
  const [imageError, setImageError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  // Размеры изображений
  const imageSizes = {
    small: { name: 'Маленький', width: '300px' },
    medium: { name: 'Средний', width: '500px' },
    large: { name: 'Большой', width: '700px' },
    full: { name: 'Полная ширина', width: '100%' },
  };

  // Выравнивания
  const alignments = {
    left: { name: 'По левому краю', style: 'float: left; margin: 0 16px 16px 0;' },
    center: { name: 'По центру', style: 'display: block; margin: 16px auto;' },
    right: { name: 'По правому краю', style: 'float: right; margin: 0 0 16px 16px;' },
  };

  const handleUrlChange = (e) => {
    const url = e.target.value;
    setImageUrl(url);
    setImageError(false);
    setImageLoaded(false);
    
    // Автоматически генерируем alt текст из URL если он пустой
    if (!altText && url) {
      const filename = url.split('/').pop().split('.')[0];
      setAltText(filename.replace(/[-_]/g, ' '));
    }
  };

  const handleImageLoad = () => {
    setImageLoaded(true);
    setImageError(false);
  };

  const handleImageError = () => {
    setImageError(true);
    setImageLoaded(false);
  };

  const handleInsertImage = () => {
    if (!imageUrl.trim()) {
      return;
    }

    const sizeStyle = imageSizes[size];
    const alignmentStyle = alignments[alignment];
    
    // Создаем HTML для изображения
    const imageHtml = `
      <div style="margin: 16px 0; text-align: ${alignment === 'center' ? 'center' : 'left'};">
        <img 
          src="${imageUrl}" 
          alt="${altText || 'Изображение'}"
          style="
            max-width: ${sizeStyle.width};
            width: ${size === 'full' ? '100%' : 'auto'};
            height: auto;
            border-radius: 8px;
            box-shadow: 0 4px 8px rgba(0,0,0,0.1);
            ${alignmentStyle.style}
          "
        />
        ${caption ? `<div style="
          font-size: 14px;
          color: #666;
          font-style: italic;
          margin-top: 8px;
          text-align: ${alignment};
        ">${caption}</div>` : ''}
      </div>
    `;

    onImageSelect(imageHtml);
    handleClose();
  };

  const handleClose = () => {
    setImageUrl('');
    setAltText('');
    setAlignment('center');
    setSize('medium');
    setCaption('');
    setImageError(false);
    setImageLoaded(false);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Вставить изображение по URL
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            label="URL изображения"
            value={imageUrl}
            onChange={handleUrlChange}
            placeholder="https://example.com/image.jpg"
            sx={{ mb: 2 }}
            helperText="Вставьте ссылку на изображение"
          />
          
          <TextField
            fullWidth
            label="Альтернативный текст"
            value={altText}
            onChange={(e) => setAltText(e.target.value)}
            sx={{ mb: 2 }}
            helperText="Описание изображения для доступности"
          />
          
          <TextField
            fullWidth
            label="Подпись (необязательно)"
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            sx={{ mb: 2 }}
            helperText="Подпись под изображением"
          />
        </Box>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Размер</InputLabel>
              <Select
                value={size}
                onChange={(e) => setSize(e.target.value)}
                label="Размер"
              >
                {Object.entries(imageSizes).map(([key, sizeOption]) => (
                  <MenuItem key={key} value={key}>
                    {sizeOption.name} ({sizeOption.width})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Выравнивание</InputLabel>
              <Select
                value={alignment}
                onChange={(e) => setAlignment(e.target.value)}
                label="Выравнивание"
              >
                {Object.entries(alignments).map(([key, alignOption]) => (
                  <MenuItem key={key} value={key}>
                    {alignOption.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {/* Предпросмотр изображения */}
        {imageUrl && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Предпросмотр:
            </Typography>
            <StyledImagePreview>
              <Box sx={{ p: 2, textAlign: alignment }}>
                <img
                  src={imageUrl}
                  alt={altText || 'Предпросмотр'}
                  onLoad={handleImageLoad}
                  onError={handleImageError}
                  style={{
                    maxWidth: imageSizes[size].width,
                    width: size === 'full' ? '100%' : 'auto',
                    height: 'auto',
                    borderRadius: '8px',
                    boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                  }}
                />
                {caption && (
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      mt: 1,
                      color: '#666',
                      fontStyle: 'italic',
                    }}
                  >
                    {caption}
                  </Typography>
                )}
              </Box>
            </StyledImagePreview>
          </Box>
        )}

        {imageError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Не удалось загрузить изображение. Проверьте URL.
          </Alert>
        )}

        {!imageUrl && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Вы можете использовать изображения из галереи статьи, локального хранилища или любые внешние ссылки.
            <br />
            Пример локального пути: /images/blog/123/my-image.jpg
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>
          Отмена
        </Button>
        <Button 
          onClick={handleInsertImage} 
          variant="contained"
          disabled={!imageUrl.trim()}
        >
          Вставить изображение
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImageUrlPicker;
